'use client'

import { toast as sonner, type ExternalToast } from 'sonner'
import { ToastCard } from '@/components/ui/toast-card'
import { permissionErrorMessage } from '@/lib/api/error-message'
import { type DedupedKind, deriveToastKey, registerToast, releaseToast } from './dedup'

/**
 * Drop-in replacement for sonner's `toast`.
 *
 * Call sites are unchanged — `toast.error('…')` works exactly as before. What
 * changes is that a toast firing again while it is still on screen updates the
 * existing card and bumps it, instead of stacking an identical second one.
 *
 * `promise` and `loading` are passed straight through: their content changes
 * loading -> settled by design, so content-derived dedup would misfire. Same for
 * `custom`, `dismiss`, and the history helpers.
 */

type Message = React.ReactNode

interface ErrorToastOptions extends ExternalToast {
  error?: unknown
}

function showError(message: Message, options?: ErrorToastOptions) {
  const { error, ...toastOptions } = options ?? {}
  return show('error', permissionErrorMessage(error) ?? message, toastOptions)
}

function show(kind: DedupedKind, message: Message, options?: ExternalToast) {
  // Sonner allows a lazy description; resolve it once so both the dedup key and
  // the rendered card see the same value.
  const description =
    typeof options?.description === 'function' ? options.description() : options?.description
  const key = deriveToastKey(kind, message, description)

  // A ReactNode title has no stable identity to key on — let sonner handle it.
  if (key === null) return sonner[kind](message, options)

  const entry = registerToast(key)
  const release = () => releaseToast(key)

  return sonner[kind](
    <ToastCard
      action={options?.action}
      actionButtonStyle={options?.actionButtonStyle}
      kind={kind}
      title={message}
      description={description}
      count={entry.count}
      onDismiss={() => sonner.dismiss(entry.id)}
    />,
    {
      ...options,
      id: entry.id,
      // The card renders its icon, content, and action itself, so sonner must
      // not render any of them as siblings outside the visible surface.
      icon: null,
      action: undefined,
      actionButtonStyle: undefined,
      description: undefined,
      // Free the key once the card is actually gone, so the next fire after
      // expiry starts a fresh toast rather than resurrecting a stale count.
      onDismiss: (dismissed) => {
        release()
        options?.onDismiss?.(dismissed)
      },
      onAutoClose: (closed) => {
        release()
        options?.onAutoClose?.(closed)
      },
    }
  )
}

export const toast = Object.assign(
  (message: Message, options?: ExternalToast) => show('message', message, options),
  {
    success: (message: Message, options?: ExternalToast) => show('success', message, options),
    error: showError,
    info: (message: Message, options?: ExternalToast) => show('info', message, options),
    warning: (message: Message, options?: ExternalToast) => show('warning', message, options),
    message: (message: Message, options?: ExternalToast) => show('message', message, options),

    // Pass-through: own their lifecycle, or have no dedupable content.
    custom: sonner.custom,
    promise: sonner.promise,
    loading: sonner.loading,
    dismiss: sonner.dismiss,
    getHistory: sonner.getHistory,
    getToasts: sonner.getToasts,
  }
)

export { ToastCard } from '@/components/ui/toast-card'
export type { DedupedKind } from './dedup'
